'use strict';

// ── cart-notification custom element ─────────────────────
// Gives product-form.js something to hook into.
// Without this element in the DOM, product-form.js redirects to /cart.
if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', class extends HTMLElement {
    getSectionsToRender() { return []; }
    setActiveElement() {}
    renderContents(parsedState) { ChCart.refresh(parsedState); }
  });
}

// ── ChCart ────────────────────────────────────────────────
const ChCart = {

  cachedCart: null, // last known cart state

  getStrings() {
    const popup = document.getElementById('ChCartPopup');
    if (!popup) {
      return {
        itemSingular: '',
        itemPlural: '',
        emptyMessage: '',
        removeLabel: '',
        quantityLabel: '',
        checkoutLabel: '',
      };
    }

    return {
      itemSingular: popup.dataset.itemSingular || '',
      itemPlural: popup.dataset.itemPlural || '',
      emptyMessage: popup.dataset.emptyMessage || '',
      removeLabel: popup.dataset.removeLabel || '',
      quantityLabel: popup.dataset.quantityLabel || '',
      checkoutLabel: popup.dataset.checkoutLabel || '',
    };
  },

  formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(cents / 100);
  },

  // Fetch fresh cart, update badges + popup if open
  fetchCart() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(data => {
        this.cachedCart = data;
        this.updateBadges(data.item_count, data.total_price);
        return data;
      });
  },

  _pendingCard: null,

  // Called by cart-notification after product-form.js adds an item
  refresh(addedItem) {
    this.fetchCart().then(data => {
      this.renderItems(data);
      this.showCardConfirm();
    });
  },

  // ── Per-card confirmation overlay ────────────────────────
  showCardConfirm() {
    const card = this._pendingCard;
    this._pendingCard = null;
    if (!card) return;
    const overlay = card.querySelector('.ch-card-confirm');
    if (overlay) {
      overlay.removeAttribute('hidden');
      overlay.setAttribute('aria-hidden', 'false');
    }
  },

  // ── Badges ───────────────────────────────────────────────
  updateBadges(count, totalPrice) {
    const strings = this.getStrings();
    const bubble = document.querySelector('.cart-count-bubble');
    if (bubble) {
      const n = bubble.querySelector('[aria-hidden]');
      if (n) n.textContent = count > 99 ? '99+' : count;
      bubble.classList.toggle('ch-badge-hidden', count === 0);
    }

    const stickyBadge = document.getElementById('ChStickyBadge');
    if (stickyBadge) {
      stickyBadge.textContent = count > 99 ? '99+' : count;
      stickyBadge.style.display = count > 0 ? '' : 'none';
    }

    const countEl = document.getElementById('ChCartCount');
    if (countEl) countEl.textContent = count;

    const itemLabelEl = document.getElementById('ChCartItemLabel');
    if (itemLabelEl) itemLabelEl.textContent = count === 1 ? strings.itemSingular : strings.itemPlural;

    const checkoutBtn = document.getElementById('ChCartCheckout');
    if (checkoutBtn && totalPrice != null) {
      checkoutBtn.textContent = `${strings.checkoutLabel} — ${this.formatMoney(totalPrice)}`;
    }
  },

  // ── Items ────────────────────────────────────────────────
  renderItems(data) {
    const el = document.getElementById('ChCartItems');
    if (!el) return;
    const strings = this.getStrings();

    if (!data.items || data.items.length === 0) {
      el.innerHTML = `<p class="ch-cart-popup__empty">${strings.emptyMessage}</p>`;
      return;
    }

    el.innerHTML = data.items.map(item => `
      <div class="ch-cart-popup__item">
        <a href="${item.url}" class="ch-cart-popup__img-link" tabindex="-1" aria-hidden="true">
          <img src="${item.image || ''}" alt="" class="ch-cart-popup__img" width="70" height="70" loading="lazy">
        </a>
        <div class="ch-cart-popup__item-body">
          <a href="${item.url}" class="ch-cart-popup__item-title">${item.product_title || item.title}</a>
          ${item.variant_title ? `<p class="ch-cart-popup__item-variant">${item.variant_title}</p>` : ''}
          <div class="ch-cart-popup__item-row">
            <button type="button" class="ch-cart-popup__remove" data-key="${item.key}">${strings.removeLabel}</button>
            <span class="ch-cart-popup__sep">|</span>
            <span class="ch-cart-popup__qty">${strings.quantityLabel} ${item.quantity}</span>
          </div>
        </div>
        <span class="ch-cart-popup__item-price">${this.formatMoney(item.final_line_price)}</span>
      </div>
    `).join('<hr class="ch-cart-popup__divider">');

    el.querySelectorAll('.ch-cart-popup__remove').forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.key));
    });
  },

  removeItem(key) {
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(r => r.json())
    .then(data => {
      this.cachedCart = data;
      this.updateBadges(data.item_count, data.total_price);
      this.renderItems(data);
    });
  },

  // ── Position popup below cart icon ───────────────────────
  positionPopup() {
    const popup = document.getElementById('ChCartPopup');
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!popup || !cartIcon) return;

    const rect = cartIcon.getBoundingClientRect();
    popup.style.top = (rect.bottom + 10) + 'px';
    popup.style.right = (window.innerWidth - rect.right) + 'px';
    popup.style.left = 'auto';
  },

  // ── Popup open / close ───────────────────────────────────
  openPopup() {
    const popup = document.getElementById('ChCartPopup');
    if (!popup) return;
    popup.removeAttribute('hidden');
    popup.offsetHeight; // force reflow for CSS transition
    popup.classList.add('ch-cart-popup--open');
  },

  closePopup() {
    const popup = document.getElementById('ChCartPopup');
    if (!popup) return;
    popup.classList.remove('ch-cart-popup--open');
    popup.addEventListener('transitionend', () => {
      if (!popup.classList.contains('ch-cart-popup--open')) {
        popup.setAttribute('hidden', '');
      }
    }, { once: true });
  },

  isOpen() {
    const popup = document.getElementById('ChCartPopup');
    return popup ? popup.classList.contains('ch-cart-popup--open') : false;
  },

  // Open instantly with cache, then silently refresh.
  // Caller must set popup position before calling this.
  openInstant() {
    if (this.cachedCart) {
      this.renderItems(this.cachedCart);
      this.openPopup();
      this.fetchCart().then(data => this.renderItems(data));
    } else {
      this.fetchCart().then(data => {
        this.renderItems(data);
        this.openPopup();
      });
    }
  },

  // ── Header cart icon hover ───────────────────────────────
  initHeaderHover() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const popup = document.getElementById('ChCartPopup');
    if (!cartIcon || !popup) return;

    let timer;

    cartIcon.addEventListener('mouseenter', () => {
      clearTimeout(timer);
      this.positionPopup(); // position below cart icon, then open
      this.openInstant();
    });

    const scheduleClose = () => {
      timer = setTimeout(() => {
        if (!popup.matches(':hover') && !cartIcon.matches(':hover')) {
          this.closePopup();
        }
      }, 200);
    };

    cartIcon.addEventListener('mouseleave', scheduleClose);
    popup.addEventListener('mouseenter', () => clearTimeout(timer));
    popup.addEventListener('mouseleave', scheduleClose);
  },

  // ── Sticky cart button ───────────────────────────────────
  initSticky() {
    const sticky = document.getElementById('ChStickyCart');
    const stickyBtn = document.getElementById('ChStickyCartBtn');
    if (!sticky) return;

    const header = document.querySelector('.site-header-wrapper');

    const onScroll = () => {
      const threshold = header ? header.offsetHeight + 20 : 150;
      sticky.hidden = window.scrollY <= threshold;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (this.isOpen()) {
          this.closePopup();
        } else {
          // Position above the sticky button (bottom-right)
          const popup = document.getElementById('ChCartPopup');
          const btnRect = stickyBtn.getBoundingClientRect();
          if (popup) {
            popup.style.top = 'auto';
            popup.style.left = 'auto';
            popup.style.bottom = (window.innerHeight - btnRect.top + 10) + 'px';
            popup.style.right = (window.innerWidth - btnRect.right) + 'px';
          }
          this.openInstant();
        }
      });
    }
  },

  // ── Outside click ────────────────────────────────────────
  initOutsideClick() {
    document.addEventListener('click', e => {
      if (!this.isOpen()) return;
      const popup = document.getElementById('ChCartPopup');
      const cartIcon = document.getElementById('cart-icon-bubble');
      const stickyBtn = document.getElementById('ChStickyCartBtn');
      if (popup && popup.contains(e.target)) return;
      if (cartIcon && cartIcon.contains(e.target)) return;
      if (stickyBtn && stickyBtn.contains(e.target)) return;
      this.closePopup();
    });
  },

  // ── Init ─────────────────────────────────────────────────
  init() {
    // Track which card triggered add-to-cart
    document.addEventListener('click', e => {
      const btn = e.target.closest('.quick-add__submit');
      if (btn) this._pendingCard = btn.closest('.ch-product-card-wrapper');
    });

    // Close card confirmation overlays
    document.addEventListener('click', e => {
      const btn = e.target.closest('.ch-card-confirm__btn--fermer, .ch-card-confirm__close');
      if (!btn) return;
      const overlay = btn.closest('.ch-card-confirm');
      if (overlay) {
        overlay.setAttribute('hidden', '');
        overlay.setAttribute('aria-hidden', 'true');
      }
    });

    const closeBtn = document.getElementById('ChCartPopupClose');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closePopup());

    // Sync badge with server-rendered count
    const bubble = document.querySelector('.cart-count-bubble');
    const initialCount = bubble
      ? parseInt(bubble.querySelector('[aria-hidden]')?.textContent || '0', 10)
      : 0;
    if (initialCount === 0 && bubble) bubble.classList.add('ch-badge-hidden');

    // Pre-fetch cart so first hover is instant
    this.fetchCart();

    this.initHeaderHover();
    this.initSticky();
    this.initOutsideClick();
  }
};

document.addEventListener('DOMContentLoaded', () => ChCart.init());
